import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaExclamationTriangle, FaTruck } from 'react-icons/fa';
import { useTranslation } from 'i18n/TranslationProvider';

const STATUS_STYLES = {
  pending: { colorScheme: 'orange', label: 'Pending review' },
  approved: { colorScheme: 'green', label: 'Approved' },
  rejected: { colorScheme: 'red', label: 'Rejected' },
  disabled: { colorScheme: 'gray', label: 'Disabled' },
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};

const getExpiryState = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'missing';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  if (date < today) return 'expired';

  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays <= 14) return 'soon';
  return 'valid';
};

const TruckCard = ({ truck, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue(
    'rgba(255, 255, 255, 0.82)',
    'rgba(14, 24, 54, 0.78)',
  );
  const cardBorder = useColorModeValue(
    'rgba(255, 255, 255, 0.95)',
    'rgba(255, 255, 255, 0.12)',
  );
  const titleColor = useColorModeValue('navy.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const metaBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const warningColor = useColorModeValue('orange.600', 'orange.300');

  const status = truck.validationStatus || 'pending';
  const statusConfig = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const expiryDate = formatDate(truck.cmrInsuranceExpiryDate);
  const expiryState = getExpiryState(truck.cmrInsuranceExpiryDate);
  const capacityLabel = truck.carCapacity
    ? `${truck.carCapacity} ${t('cars')}`
    : t('Not set');
  const maxLoadLabel = truck.maxLoadKg
    ? `${truck.maxLoadKg} ${t('kg')}`
    : t('Not set');

  return (
    <Box
      p={{ base: 4, md: 5 }}
      borderWidth="1px"
      borderColor={cardBorder}
      borderRadius="18px"
      bg={cardBg}
      backdropFilter="blur(6px)"
      boxShadow="0 18px 36px rgba(12, 28, 70, 0.12)"
      w="100%"
    >
      <Flex
        direction={{ base: 'column', xl: 'row' }}
        align={{ base: 'stretch', xl: 'center' }}
        justify="space-between"
        gap={4}
      >
        <HStack spacing={3} minW={{ xl: '260px' }}>
          <Flex
            w={10}
            h={10}
            borderRadius="12px"
            bg={metaBg}
            align="center"
            justify="center"
          >
            <Icon as={FaTruck} w={5} h={5} color="blue.400" />
          </Flex>
          <Stack spacing={0.5}>
            <Text fontWeight="700" fontSize={{ base: 'md', md: 'lg' }} color={titleColor}>
              {truck.licensePlate}
            </Text>
            <Text color={textColor} fontSize="xs">
              {t('Truck license plate')}
            </Text>
          </Stack>
        </HStack>

        <HStack
          flexWrap="wrap"
          spacing={2.5}
          align="center"
          justify={{ base: 'flex-start', xl: 'center' }}
        >
          <HStack px={3} py={1.5} borderRadius="12px" bg={metaBg}>
            <Icon as={FaCalendarAlt} color={textColor} />
            <Text color={textColor} fontSize="sm">
              {t('CMR Expiry')}: {expiryDate || t('Pending admin review')}
            </Text>
          </HStack>
          <HStack px={3} py={1.5} borderRadius="12px" bg={metaBg}>
            <Text color={textColor} fontSize="sm">
              {t('Capacity')}: {capacityLabel}
            </Text>
          </HStack>
          <HStack px={3} py={1.5} borderRadius="12px" bg={metaBg}>
            <Text color={textColor} fontSize="sm">
              {t('Weight (kg)')}: {maxLoadLabel}
            </Text>
          </HStack>

          <Badge
            colorScheme={statusConfig.colorScheme}
            borderRadius="full"
            px={2.5}
            py={1}
            textTransform="none"
            fontSize="xs"
          >
            {t(statusConfig.label)}
          </Badge>
        </HStack>

        <HStack
          justify={{ base: 'stretch', md: 'flex-end' }}
          spacing={2}
          w={{ base: '100%', md: 'auto' }}
        >
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(truck)}
            px={4}
            w={{ base: '100%', md: 'auto' }}
          >
            {t('Edit')}
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            px={4}
            onClick={() => onDelete(truck._id)}
            w={{ base: '100%', md: 'auto' }}
          >
            {t('Delete')}
          </Button>
        </HStack>
      </Flex>

      {(expiryState === 'expired' || expiryState === 'soon') && (
        <HStack mt={3} spacing={2} color={warningColor}>
          <Icon as={FaExclamationTriangle} boxSize={3.5} />
          <Text fontSize="xs" fontWeight="600">
            {expiryState === 'expired'
              ? t('CMR insurance has expired. Upload a new CMR Insurance for review.')
              : t('CMR insurance is expiring within 14 days.')}
          </Text>
        </HStack>
      )}

      {truck.cmrLastRejectedReason && status === 'rejected' && (
        <Text mt={2} fontSize="xs" color="red.400">
          {t('Rejection reason')}: {truck.cmrLastRejectedReason}
        </Text>
      )}
    </Box>
  );
};

export default TruckCard;
